import { Command } from 'cmdk'

import {
  AllChannelsIcon,
  ChannelAvatar,
  Checkbox,
  DropdownMenu,
  Flex,
  Input,
  PlusIcon,
  SearchIcon,
  Text,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import {
  ChannelSelector,
  useChannelGroupsFilter,
} from '~publish/components/ChannelSelector'
import { NO_CHANNELS_VALUE } from '~publish/components/FilterByChannel'
import { useFilterByChannelFragment } from '~publish/components/FilterByChannel'
import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'

import {
  isPublishSupported,
  PUBLISH_UNSUPPORTED,
} from '~publish/utils/permissions/publishSupport'

import styles from './ChannelFilterSubMenu.module.css'

import type { JSX } from 'react'

export type ChannelFilterSubMenuProps = {
  channels: MaskedFilterByChannelFragment
  selectedChannelIds: string[]
  onChannelSelect: (channelIds: string[]) => void
  /**
   * Channel IDs that should render disabled (non-selectable). Keeps this
   * responsive filter consistent with `FilterByChannel`, which greys out
   * channels the actor can't access.
   */
  disabledChannelIds?: Set<string>
  /**
   * Shows the "No channels" row, combinable with a real channel selection.
   * Only Publish's Drafts tab has channel-less content to filter to — other
   * surfaces omit this and default to false.
   */
  showNoChannelsOption?: boolean
}

export function ChannelFilterSubMenu({
  channels: maskedChannels,
  selectedChannelIds,
  onChannelSelect,
  disabledChannelIds,
  showNoChannelsOption = false,
}: ChannelFilterSubMenuProps): JSX.Element {
  const channels = useFilterByChannelFragment(maskedChannels)
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()

  const isNoChannelsSelected = selectedChannelIds.includes(NO_CHANNELS_VALUE)
  const selectedRealChannelIds = selectedChannelIds.filter(
    (id) => id !== NO_CHANNELS_VALUE,
  )

  // Re-appends the sentinel after a real-channel-only change, so picking
  // channels alongside "No channels" doesn't drop it (@see NO_CHANNELS_VALUE).
  const onSelectRealChannels = (channelIds: string[]): void => {
    onChannelSelect(
      isNoChannelsSelected ? [...channelIds, NO_CHANNELS_VALUE] : channelIds,
    )
  }

  // Real-channel-only, so a group toggle can never mix the sentinel into the
  // resulting selection (@see NO_CHANNELS_VALUE).
  const {
    filteredChannelGroups,
    selectedChannelGroupIds,
    disabledChannelGroupIds,
    handleSelectGroup,
    isExpanded: isChannelGroupsExpanded,
    setIsExpanded: setIsChannelGroupsExpanded,
  } = useChannelGroupsFilter({
    selectedChannelIds: selectedRealChannelIds,
    onChannelSelect: onSelectRealChannels,
    disabledChannelIds,
  })

  const toggleChannel = (channelId: string): void => {
    const newSelection = selectedRealChannelIds.includes(channelId)
      ? selectedRealChannelIds.filter((id) => id !== channelId)
      : [...selectedRealChannelIds, channelId]
    onSelectRealChannels(newSelection)
  }

  const toggleNoChannels = (): void => {
    onChannelSelect(
      isNoChannelsSelected
        ? selectedRealChannelIds
        : [...selectedRealChannelIds, NO_CHANNELS_VALUE],
    )
  }

  const handleSelectAll = (): void => {
    if (selectedRealChannelIds.length > 0) {
      onSelectRealChannels([])
    } else {
      // Never select channels the actor can't access.
      onSelectRealChannels(
        channels
          .filter((channel) => !disabledChannelIds?.has(channel.id))
          .map((channel) => channel.id),
      )
    }
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <AllChannelsIcon /> Filter by channel
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        <Command loop className={styles.command}>
          {channels.length > 0 && (
            <Command.Input asChild>
              <Input
                prefix={<SearchIcon />}
                placeholder="Search channels..."
                className={styles.searchInput}
              />
            </Command.Input>
          )}
          {/* Guard the list, not the whole command, so the search input stays
              translatable. The list holds cmdk's churning channel rows plus the
              dynamic "Select all"/"Unselect all" toggle — both crash-prone under
              translation — so guarding here keeps them safe. See FilterByTag. */}
          <Command.List className={styles.list} translate="no">
            {filteredChannelGroups.length > 0 && (
              <ChannelSelector.GroupsOverflow
                groups={filteredChannelGroups}
                selectedIds={selectedChannelGroupIds}
                disabledIds={disabledChannelGroupIds}
                onSelectGroup={handleSelectGroup}
                isExpanded={isChannelGroupsExpanded}
                onExpand={() => setIsChannelGroupsExpanded(true)}
              >
                <ChannelSelector.NewGroupLink>
                  <PlusIcon size={16} />
                  Group
                </ChannelSelector.NewGroupLink>
              </ChannelSelector.GroupsOverflow>
            )}
            {channels.length > 0 && (
              <Flex
                justify="between"
                align="center"
                className={styles.selectAllHeader}
              >
                <Text>Channels</Text>
                <UnstyledButton onClick={handleSelectAll}>
                  <Text>
                    {selectedRealChannelIds.length > 0
                      ? 'Unselect all'
                      : 'Select all'}
                  </Text>
                </UnstyledButton>
              </Flex>
            )}
            {/* Rendered directly, not via Command.Empty: cmdk only shows its
                own Empty state when the whole list's filtered item count is
                zero, but "No channels" is a registered, always-matching item
                whenever `showNoChannelsOption` is set — so that count never
                reaches zero even when there are no real channels. */}
            {channels.length === 0 && (
              <div className={styles.empty}>No channels available</div>
            )}
            {channels.length > 0 && (
              <div aria-live="polite" aria-atomic="true">
                <Command.Empty className={styles.empty}>
                  No channels found. Try a different search or clear the search
                  to see all channels.
                </Command.Empty>
              </div>
            )}
            {channels.map((channel) => {
              const isDisabled = disabledChannelIds?.has(channel.id) ?? false
              return (
                <Command.Item
                  key={channel.id}
                  value={`${channel.name} ${channel.service}`}
                  onSelect={(): void => toggleChannel(channel.id)}
                  disabled={isDisabled}
                  className={styles.item}
                >
                  <Checkbox
                    checked={selectedRealChannelIds.includes(channel.id)}
                    disabled={isDisabled}
                    tabIndex={-1}
                  />
                  <ChannelAvatar
                    size="small"
                    alt=""
                    src={channel.avatar}
                    name={channel.name}
                    channel={channel.service}
                  />
                  <Flex direction="column" className={styles.channelDetails}>
                    <span className={styles.channelName}>{channel.name}</span>
                    {isDisabled && !isPublishSupported(channel) && (
                      <Text size="sm" color="subtle">
                        {PUBLISH_UNSUPPORTED}
                      </Text>
                    )}
                  </Flex>
                </Command.Item>
              )
            })}
            {channelAgnosticDraftsEnabled && showNoChannelsOption && (
              <>
                <Command.Separator className={styles.separator} />
                <Command.Item
                  key={NO_CHANNELS_VALUE}
                  value="No channels"
                  onSelect={toggleNoChannels}
                  className={styles.item}
                >
                  <Checkbox checked={isNoChannelsSelected} tabIndex={-1} />
                  <span className={styles.channelName}>No channels</span>
                </Command.Item>
              </>
            )}
          </Command.List>
        </Command>
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}
